/**
 * Mock layer สำหรับพัฒนา/ทดสอบ UI ก่อน API ขึ้น (spec 013)
 *
 * เปิดด้วย REACT_APP_USE_MOCK=1 ใน .env.dev เท่านั้น
 * .env.prod ต้องไม่มีตัวแปรนี้
 *
 * จับคู่เฉพาะ endpoint ที่ระบุไว้ที่นี่ — endpoint อื่นยังยิงของจริงตามปกติ
 * ทำให้ mock ทีละส่วนได้โดยไม่ต้องปิดทั้งระบบ
 */
import rewardList from './fixtures/reward_list.json'
import gainTypeList from './fixtures/gain_type_list.json'
import couponReport from './fixtures/coupon_report.json'
import pointScopeList from './fixtures/point_scope_list.json'
import userPointList from './fixtures/user_point_list.json'
import pointTransaction from './fixtures/point_transaction.json'
import redeemTransaction from './fixtures/redeem_transaction.json'
import materialAliasCandidates from './fixtures/material_alias_candidates.json'
import materialAliasFixture from './fixtures/material_alias.json'
import materialSearchMiss from './fixtures/material_search_miss.json'
import materialCustomFixture from './fixtures/material_custom_list.json'
import materialStandardUnits from './fixtures/material_standard_units.json'

export const isMockEnabled = () => process.env.REACT_APP_USE_MOCK === '1'

const ok = (data) => ({ status: 200, statusText: 'OK', data, headers: {}, config: {} })

// ---------- วัสดุเฉพาะร้าน (spec 017 · backend green2get-app spec 111) ----------
// backend ห่อผลใน { data, message } และตอบ 400 พร้อมเหตุผลเมื่อปฏิเสธ
const wrapped = (data) => ok({ data, message: 'success' })
const rejected = (message) => ({
  status: 400,
  statusText: 'Bad Request',
  data: { data: false, message },
  headers: {},
  config: {}
})
const paged = (list, params) => {
  const size = Number((params && params.size) || 20)
  const page = Number((params && params.page) || 0)
  return {
    content: list.slice(page * size, page * size + size),
    total: list.length,
    page,
    size
  }
}
// ตอนที่ interceptor เรียก mock axios ยังไม่ได้แปลง body เป็น JSON string จึงรับได้ทั้งสองแบบ
const bodyOf = (config) =>
  typeof config.data === 'string' ? JSON.parse(config.data) : config.data || {}
const sameText = (a, b) =>
  String(a || '')
    .replace(/\s+/g, '')
    .toLowerCase() ===
  String(b || '')
    .replace(/\s+/g, '')
    .toLowerCase()

let materialAliases = materialAliasFixture.map((alias) => ({ ...alias }))
let materialCustomList = materialCustomFixture.map((row) => ({ ...row }))
let nextAliasId = 100

const saveAliasMock = (config) => {
  const body = bodyOf(config)
  const status = body.status || 'APPROVED'
  const existing = materialAliases.find(
    (alias) =>
      alias.materialId === body.materialId && sameText(alias.alias, body.alias)
  )
  if (existing && existing.status === status) {
    return rejected('มีคำนี้อยู่แล้วในสถานะเดียวกัน')
  }
  if (existing) {
    existing.status = status
    return wrapped(existing)
  }
  nextAliasId += 1
  const created = {
    id: nextAliasId,
    materialId: body.materialId,
    alias: body.alias,
    status,
    source: body.source || 'ADMIN',
    createdAt: new Date().toISOString()
  }
  materialAliases.push(created)
  return wrapped(created)
}

const customListMock = (config) => {
  const params = config.params || {}
  const keyword = String(params.keyword || '')
    .replace(/\s+/g, '')
    .toLowerCase()
  const list = materialCustomList
    .filter(
      (row) =>
        !params.parentMaterialId ||
        row.parentMaterialId === Number(params.parentMaterialId)
    )
    .filter(
      (row) => !params.heroId || row.ownerHeroId === Number(params.heroId)
    )
    .filter(
      (row) =>
        !keyword || row.name.replace(/\s+/g, '').toLowerCase().includes(keyword)
    )
    .sort((a, b) =>
      params.sort === 'createdAt'
        ? b.createdAt.localeCompare(a.createdAt)
        : b.sameNameShopCount - a.sameNameShopCount ||
          b.createdAt.localeCompare(a.createdAt)
    )
  return wrapped(paged(list, params))
}

const updateCustomParentMock = (config) => {
  const body = bodyOf(config)
  const row = materialCustomList.find((item) => item.id === body.id)
  if (!row) {
    return rejected('ไม่พบวัสดุ')
  }
  // วัสดุที่ mock ไม่รู้จัก (มาจาก /material/all ของจริง) ถือว่าหน่วยตรงกัน
  const parent = materialStandardUnits.find(
    (item) => item.id === body.parentMaterialId
  )
  if (parent && parent.unit !== row.unit) {
    return rejected(
      `หน่วยไม่ตรงกัน: วัสดุของร้านใช้ "${row.unit}" แต่วัสดุที่จะผูกใช้ "${parent.unit}"`
    )
  }
  row.parentMaterialId = body.parentMaterialId
  row.parentMaterialName = parent
    ? parent.name
    : `วัสดุ #${body.parentMaterialId}`
  return wrapped('success')
}

const promoteCustomMock = (config) => {
  const body = bodyOf(config)
  if (materialStandardUnits.some((item) => sameText(item.name, body.name))) {
    return rejected(`มีวัสดุมาตรฐานชื่อ "${body.name}" อยู่แล้ว`)
  }
  materialCustomList = materialCustomList.filter((item) => item.id !== body.id)
  return wrapped('success')
}

// [method, regex ของ path, ตัวสร้าง response]
const handlers = [
  ['get', /\/reward\/getAll$/, () => ok(rewardList)],
  ['get', /\/reward\/admin\/getAllGainPointType$/, () => ok(gainTypeList)],
  ['get', /\/reward\/(\d+)$/, (match) => {
    const id = Number(match[1])
    return ok(rewardList.find((reward) => reward.id === id) || rewardList[0])
  }],
  ['get', /\/reward\/(\d+)\/scope-rule$/, (match) => {
    const id = Number(match[1])
    const reward = rewardList.find((item) => item.id === id)
    return ok(reward ? reward.scopeRules : [])
  }],
  ['put', /\/reward\/(\d+)\/scope-rule$/, (match, config) => {
    const id = Number(match[1])
    const reward = rewardList.find((item) => item.id === id)
    if (reward) {
      reward.scopeRules = JSON.parse(config.data || '[]')
    }
    return ok({ message: 'Success' })
  }],
  ['get', /\/coupon\/admin\/report$/, () => ok(couponReport)],
  ['get', /\/reward\/admin\/point-scope$/, () => ok(pointScopeList)],
  ['get', /\/reward\/admin\/getAllUserPoint$/, () => ok(userPointList)],
  ['get', /\/reward\/admin\/searchUserPoint/, () => ok({ content: userPointList, totalElements: userPointList.length })],
  ['post', /\/reward\/admin\/grantPoint$/, () => ok({ message: 'แจกพอยท์สำเร็จ' })],
  ['get', /\/reward\/searchUserGainPointTransaction/, () => ok(pointTransaction)],
  ['get', /\/reward\/admin\/redeemHistory$/, () => ok(redeemTransaction)],
  [
    'get',
    /\/material\/aliasCandidates$/,
    (match, config) =>
      wrapped(
        paged(
          // คู่ที่แอดมินตัดสินแล้วไม่กลับมาในรายการรออนุมัติ
          materialAliasCandidates.filter(
            (candidate) =>
              !materialAliases.some(
                (alias) =>
                  alias.materialId === candidate.materialId &&
                  sameText(alias.alias, candidate.alias)
              )
          ),
          config.params
        )
      )
  ],
  [
    'get',
    /\/material\/alias$/,
    (match, config) =>
      wrapped(
        materialAliases.filter(
          (alias) => alias.materialId === Number(config.params.materialId)
        )
      )
  ],
  ['post', /\/material\/alias$/, (match, config) => saveAliasMock(config)],
  [
    'delete',
    /\/material\/alias\/(\d+)$/,
    (match) => {
      materialAliases = materialAliases.filter(
        (alias) => alias.id !== Number(match[1])
      )
      return wrapped('success')
    }
  ],
  ['get', /\/material\/searchMiss$/, () => wrapped(materialSearchMiss)],
  ['get', /\/material\/customList$/, (match, config) => customListMock(config)],
  [
    'put',
    /\/material\/updateCustomParent$/,
    (match, config) => updateCustomParentMock(config)
  ],
  [
    'post',
    /\/material\/promoteCustom$/,
    (match, config) => promoteCustomMock(config)
  ]
]

/**
 * คืน response ปลอมถ้ามี handler ที่ตรง ไม่งั้นคืน null เพื่อให้ยิงของจริงต่อ
 */
export const resolveMock = (config) => {
  if (!isMockEnabled()) {
    return null
  }

  const method = (config.method || 'get').toLowerCase()
  const url = config.url || ''

  for (const [handlerMethod, pattern, build] of handlers) {
    if (handlerMethod !== method) {
      continue
    }
    const match = url.match(pattern)
    if (match) {
      // eslint-disable-next-line no-console
      console.info('[MOCK]', method.toUpperCase(), url)
      return build(match, config)
    }
  }

  return null
}
